import { Alert, AlertTitle, Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { FunctionComponent } from 'react'
import { CrpEmissionsTable } from './crp-emissions-table'
import { ICrpFigures } from '../../../../shared/services/crp-report/crp-report.model'

const fmt = (value: number) => Number(value ?? 0).toLocaleString('en-GB', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

/**
 * Step 2 — the figures as they will appear in the published plan. Missing required Scope 3 categories
 * are surfaced prominently but never block generation: an incomplete plan the supplier knows about is
 * more use than no plan, and PPN 006 expects gaps to be visible rather than reported as zero.
 */
export const CrpStepEmissionsReview: FunctionComponent<{ figures: ICrpFigures }> = ({ figures }) => {
  const { baseline, current } = figures.periods
  const isProgress = figures.planType === 'PROGRESS'

  const missing = Array.from(new Set([
    ...(figures.missingRequiredCategories.baseline || []),
    ...(figures.missingRequiredCategories.current || []),
  ])).sort((a, b) => a - b)

  const missingLabels = missing.map((number) => {
    const match = baseline?.scope3.find((c) => c.number === number)
      || current?.scope3.find((c) => c.number === number)
    return `Category ${number}${match ? `: ${match.label}` : ''}`
  })

  return (
    <>
      <Typography variant='h6' sx={{ mb: 1 }}>Review your emissions</Typography>
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
        These are the figures that will appear in your plan. They are calculated the same way as your SECR and
        greenhouse gas reports for the same years and basis, so they will match those reports exactly.
      </Typography>

      {figures.baselineEnteredManually && (
        <Alert severity='info' sx={{ mb: 3 }}>
          Your baseline is the base year you entered manually, so its figures come from those entries rather
          than calculated emissions.{' '}
          {(baseline?.scope3.length ?? 0) > 0
            ? 'Its Scope 3 shows the category breakdown you entered; anything you did not break down appears under other categories.'
            : 'Its Scope 3 was recorded as a single total, so it appears under other categories instead of a category breakdown. You can add a breakdown by editing the base year in your reporting periods.'}
          {' '}The published plan notes this.
        </Alert>
      )}

      {missing.length > 0 && (
        <Alert severity='warning' sx={{ mb: 3 }}>
          <AlertTitle>Required Scope 3 categories with no data</AlertTitle>
          <Typography variant='body2' sx={{ mb: 1 }}>
            PPN 006 requires these categories. They have no recorded emissions for the years selected, so they
            will be shown as &ldquo;Not reported&rdquo; in your plan rather than as zero.
          </Typography>
          <Box component='ul' sx={{ pl: 2.5, mb: 1 }}>
            {missingLabels.map((label) => (
              <Typography component='li' variant='body2' key={label}>{label}</Typography>
            ))}
          </Box>
          <Typography variant='body2'>
            You can continue and publish with these gaps flagged, or add the missing data first and come back.
          </Typography>
        </Alert>
      )}

      {isProgress
        ? <ProgressTable figures={figures} />
        : (
          <>
            <Typography variant='subtitle1' sx={{ mb: 1 }}>
              Baseline year: {baseline?.label || '—'}
            </Typography>
            <Box sx={{ mb: 4 }}>
              <CrpEmissionsTable period={baseline} />
            </Box>

            <Typography variant='subtitle1' sx={{ mb: 1 }}>
              Current reporting year: {current?.label || '—'}
            </Typography>
            <Box sx={{ mb: 3 }}>
              <CrpEmissionsTable period={current} />
            </Box>
          </>
        )}

      <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>
        {figures.methodologyText}
      </Typography>
    </>
  )
}

/** 2030 progress report — one column per reporting year, plus change against the baseline. */
const ProgressTable: FunctionComponent<{ figures: ICrpFigures }> = ({ figures }) => {
  const series = figures.series || []
  const rows = figures.progressRows || []

  if (!rows.length) {
    return (
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
        No reporting periods were found after the baseline year, so there is no progress to report yet.
      </Typography>
    )
  }

  return (
    <Box sx={{ overflowX: 'auto', mb: 3 }}>
      <Table size='small' sx={{ minWidth: 640 }}>
        <TableHead>
          <TableRow>
            <TableCell>Emissions</TableCell>
            {series.map((period) => (
              <TableCell align='right' key={period.reportPeriodId}>{period.label}</TableCell>
            ))}
            <TableCell align='right'>Change vs baseline</TableCell>
            <TableCell align='right'>Change %</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const emphasise = row.key === 'total' || row.key === 'scopeThree'
            const cellSx = emphasise ? { fontFamily: 'EffraMd' } : undefined
            return (
              <TableRow key={row.key}>
                <TableCell sx={cellSx}>{row.label}</TableCell>
                {row.values.map((value, index) => (
                  <TableCell align='right' key={`${row.key}-${index}`} sx={cellSx}>{fmt(value)}</TableCell>
                ))}
                <TableCell align='right' sx={cellSx}>{fmt(row.changeTco2e)}</TableCell>
                <TableCell align='right' sx={cellSx}>
                  {row.changePercent == null
                    ? '—'
                    : (
                      <Chip
                        size='small'
                        variant='outlined'
                        color={row.changePercent <= 0 ? 'success' : 'error'}
                        label={`${row.changePercent > 0 ? '+' : ''}${row.changePercent}%`}
                      />
                    )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
}
